import {Fragment, useEffect, useRef, useState} from 'react';
// @ts-expect-error @headlessui/react incompatibility with node16 resolution
import {Dialog, Transition} from '@headlessui/react';
import clsx from 'clsx';
import {
  CloseMenuIcon,
  DurationIcon,
  ProductPrices,
  SparkleSingleIcon,
  RoundedModal,
  IFormInput,
  useDrawerContext,
} from '~/components';
import {Product as ProductType} from '@shopify/hydrogen/storefront-api-types';
import {ArrowLeftIcon} from '../icons/ArrowLeftIcon';
import {AddToCartButton, useProductOptions} from '@shopify/hydrogen';
import {
  ProductSelectService,
  getValueProperty,
} from './ProductSelectService.client';
import {ProductPersonalizeGift} from './ProductPersonalizeGift.client';
import {format} from 'date-fns';
import {useForm} from 'react-hook-form';
import {scaleWidth} from '~/utils';
import {useMobile} from '~/hooks';

interface Props {
  isOpen: boolean;
  isGift: boolean;
  product: ProductType;
  onClose: () => void;
}

const STEP_SERVICE = 0;
const STEP_PERSONALIZE = 1;

export function ProductGiftingSteps({isOpen, isGift, product, onClose}: Props) {
  const {selectedOptions, selectedVariant} = useProductOptions();
  const [currentStep, setCurrentStep] = useState<number>(STEP_SERVICE);
  const [closeConfirmModal, setCloseConfirmModal] = useState<boolean>(false);
  const [giftDate, setGiftDate] = useState<Date>(new Date());
  const [selectedCard, setSelectedCard] = useState<string>(
    '/images/gift-cards/01_HBD GREETING CARD.png',
  );
  const {setIsOpenCart} = useDrawerContext();
  const isMobile = useMobile();

  const productTitleArr = (product?.title ?? 'Product Title').split(' ');
  const productTime = product?.productType?.split('|')[0]?.trim() ?? '';
  const giftingStepRef = useRef<HTMLDivElement>(null);

  const {
    register,
    setValue,
    watch,
    formState: {isValid},
  } = useForm<IFormInput>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(STEP_SERVICE);
    }
  }, [isOpen]);

  const renderStickyProductInfo = () => (
    <div
      className="border-t-2 border-clay-100 bg-[#FAFAFA] py-[16px]"
      style={{paddingLeft: scaleWidth(40), paddingRight: scaleWidth(40)}}
    >
      {currentStep == STEP_PERSONALIZE && (
        <div className="text-[18px] leading-none text-acai-900">
          Gift Summary
        </div>
      )}
      <div
        className={clsx(
          'mt-2 flex items-center',
          currentStep == STEP_SERVICE && 'justify-between',
        )}
      >
        {/* Begin Product Title */}
        <div
          className="font-semibold leading-none text-[#4B2304]"
          style={{fontSize: scaleWidth(14)}}
        >
          {productTitleArr[0]}{' '}
          {productTitleArr[1] == 'Glow' && productTitleArr[1]}
          {productTitleArr[1] == 'a' && productTitleArr[1]}
          {productTitleArr.length > 1 && (
            <>
              {' '}
              <span className="font-serif italic">
                {productTitleArr
                  .filter(
                    (str, index) =>
                      index !== 0 && str !== 'Glow' && str !== 'a',
                  )
                  .join(' ')}
              </span>
            </>
          )}
        </div>
        {/* End Product Title */}
        {currentStep == STEP_PERSONALIZE ? (
          <div className="ml-2 text-[12px] text-[#70655C]">{productTime}</div>
        ) : (
          <div className="text-[12px]">
            <div className="flex items-center text-[#70655C]">
              <DurationIcon width={16} height={16} />
              <div className="ml-[4px]">{productTime}</div>
            </div>
          </div>
        )}
      </div>
      {/* Begin Service Info */}
      <div
        className="mt-[8px] leading-[119%] text-[#4B2304]"
        style={{fontSize: scaleWidth(14)}}
      >
        {selectedOptions
          ? Object.values(selectedOptions)
              .filter((item) => item !== '-')
              .map((item, index) => (
                <span key={index}>
                  {index > 0 && <span className="mx-1">/</span>}
                  <span>{getValueProperty(item ?? '').title}</span>
                </span>
              ))
          : null}
      </div>
      {/* End Service Info */}
      {/* Begin SendDate Info */}
      {currentStep == STEP_PERSONALIZE && (
        <div
          className="mt-[6px] text-acai-900"
          style={{fontSize: scaleWidth(14)}}
        >
          Email Send Date: {format(giftDate, 'EEE, d MMMM yyyy')}
        </div>
      )}
      {/* End SendDate Info */}
      {/* Begin Price Info */}
      <div
        className="mt-[8px] flex items-center gap-2 font-bold text-acai-900"
        style={{fontSize: scaleWidth(14)}}
      >
        <ProductPrices hasStartFrom={currentStep !== STEP_PERSONALIZE} />
      </div>
      {/* End Price Info */}
      {/* Begin Next Step Button */}
      <div className="mt-[18px]">
        {currentStep === STEP_SERVICE ? (
          <button
            className={
              'flex w-full items-center justify-center rounded-full border border-daybreak-400 bg-daybreak-400 py-[16px] text-center text-[14px] leading-none text-white duration-300 active:bg-daybreak-400 active:text-white disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300'
            }
            onClick={() => {
              setCurrentStep(currentStep + 1);
              giftingStepRef.current?.scrollTo({top: 0});
            }}
          >
            Add Service
          </button>
        ) : (
          <AddToCartButton
            type="submit"
            className={clsx(
              'flex w-full items-center justify-center rounded-full border border-daybreak-400 bg-daybreak-400 py-[16px] text-center text-[14px] leading-none text-white duration-300 active:bg-daybreak-400 active:text-white disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-300',
            )}
            variantId={selectedVariant?.id}
            attributes={[
              {
                key: 'Gift Send Date',
                value: `Your gift to ${
                  watch('to_name') as string
                  //Stays local here
                } will be sent on ${format(giftDate, 'MMM d yyy')}`,
              },
              {
                key: 'Recipient Email',
                value: watch('email') as string,
              },
              {
                key: '_start_time',
                value: giftDate.toISOString(),
              },
              //Add an extra attribute which is just the date to send the gift, ensuring timezones dont come into play
              {
                key: '_gift_send_date',
                //Keep it local, so dates dont change
                value: format(giftDate, 'dd-MM-yyyy'),
              },
              {
                key: '_timezone',
                value: 'Singapore',
              },
              {
                key: '_tfr_booking',
                value: 'false',
              },
              {
                key: '_tfr_is_gift',
                value: isGift ? 'true' : 'false',
              },
              {
                key: '_gift_to_name',
                value: watch('to_name') as string,
              },
              {
                key: '_gift_to_email',
                value: watch('email') as string,
              },
              {
                key: '_gift_from_name',
                value: watch('from_name') as string,
              },
              {
                key: '_gift_from_email',
                value: watch('from_email') as string,
              },
              {
                key: '_gift_message',
                value: watch('message') as string,
              },
              {
                key: '_gift_card',
                value: selectedCard,
              },
            ]}
            disabled={!isValid}
            onClick={() => {
              setIsOpenCart(true);
              onClose();
            }}
          >
            <div className="flex items-center">
              <div>Add to Cart</div>
              <div className="mx-2">
                <SparkleSingleIcon
                  fill="#ffffff"
                  width={9}
                  height={8}
                  viewBox="0 0 13 12"
                />
              </div>
              <div>
                <ProductPrices forBookNow />
              </div>
            </div>
          </AddToCartButton>
        )}
      </div>
      {/* End Next Step Button */}
    </div>
  );

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="h-full w-full text-center md:flex md:items-center md:justify-center md:p-4 md:pt-[80px]">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={clsx(
                  'booking-steps-wrapper',
                  !isMobile && 'max-h-[800px] max-w-[450px]',
                )}
              >
                <StepProgressBar
                  step={currentStep}
                  onPrevious={() => {
                    if (currentStep == STEP_SERVICE) {
                      setCloseConfirmModal(true);
                    } else {
                      setCurrentStep(currentStep - 1);
                      giftingStepRef.current?.scrollTo({top: 0});
                    }
                  }}
                  onClose={() => {
                    setCloseConfirmModal(true);
                  }}
                />
                <div
                  className="flex-grow overflow-auto py-[8px]"
                  ref={giftingStepRef}
                  style={{
                    paddingLeft: scaleWidth(40),
                    paddingRight: scaleWidth(40),
                  }}
                >
                  {currentStep == STEP_SERVICE ? (
                    <ProductSelectService />
                  ) : currentStep == STEP_PERSONALIZE ? (
                    <ProductPersonalizeGift
                      register={register}
                      setValue={setValue}
                      giftDate={giftDate}
                      setGiftDate={setGiftDate}
                      setSelectedCard={setSelectedCard}
                    />
                  ) : null}
                </div>
                {renderStickyProductInfo()}
                <RoundedModal
                  isOpen={closeConfirmModal}
                  onClose={() => {
                    setCloseConfirmModal(false);
                  }}
                  onConfirm={() => {
                    setCloseConfirmModal(false);
                    onClose();
                  }}
                  confirmButtonText="Exit"
                >
                  <div
                    className="py-8"
                    style={{
                      paddingLeft: scaleWidth(24),
                      paddingRight: scaleWidth(24),
                    }}
                  >
                    <div
                      className="text-[22px] italic text-daybreak-400"
                      style={{fontFamily: 'GT-Alpina'}}
                    >
                      Oops!
                    </div>
                    <div
                      className="mt-4 tracking-tight"
                      style={{
                        fontSize: scaleWidth(15),
                        letterSpacing: -scaleWidth(0.3),
                      }}
                    >
                      Your Refresh isn&apos;t ready to go just yet.{'\n'} Click
                      &apos;Back&apos; to continue your booking.
                    </div>
                  </div>
                </RoundedModal>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

interface StepProgressBarProps {
  step: number;
  onClose: () => void;
  onPrevious: () => void;
}

function StepProgressBar({step, onClose, onPrevious}: StepProgressBarProps) {
  const steps = [STEP_SERVICE, STEP_PERSONALIZE];
  const stepTitles = ['Select Service', 'Personalize Gift'];

  return (
    <div>
      <div className="grid w-full grid-cols-2 gap-2">
        {steps.map((item) => (
          <div key={'step' + item} className="w-full bg-clay-100">
            <div
              className={clsx(
                'h-2 bg-daybreak-400 transition-all',
                item <= step ? 'w-full' : 'w-0',
              )}
            />
          </div>
        ))}
      </div>
      <div
        className="py-[16px]"
        style={{paddingLeft: scaleWidth(40), paddingRight: scaleWidth(40)}}
      >
        <div className="flex items-center justify-between">
          <button onClick={onPrevious}>
            <ArrowLeftIcon width={18} height={18} />
          </button>
          <div className="text-[13px] text-acai-900">
            {step + 1} - {stepTitles[step]}
          </div>
          <button onClick={onClose}>
            <CloseMenuIcon width={18} height={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
